<template>
  <div class="v-slider-wrapper" :class="{ 'is-disabled': disabled, 'is-inverted': inverted, 'label-in-tooltip': labelInTooltip }">
    <div ref="slider" class="v-slider" @mouseleave="hideTooltip" @mouseover="showTooltip">
      <input :id class="v-slider__inner" :disabled :max :min :step type="range" :value="modelValue" @input="onInput(Number(($event.target as HTMLInputElement).value))" />

      <div class="v-slider__fill" :style="{ width: `${fillWidth}%` }" />
      <VTooltip ref="tooltip" :content="String(modelValue)" lazy :reference-style trigger="manual">
        <div class="v-slider__button" />
      </VTooltip>
    </div>
    <label v-if="!labelInTooltip" :for="id" v-text="modelValue" />
    <VIconHelp v-if="help" :help type="placeholder" />
  </div>
</template>

<script setup lang="ts">
import { computed, type CSSProperties, onMounted, ref, useId, useTemplateRef } from 'vue'

import { useResizeObserver } from '../composables/useResizeObserver.ts'
import VIconHelp from './VIconHelp.vue'
import VTooltip from './VTooltip.vue'

interface Props {
  /** Same as HTML `input` min attribute. */
  min?: number

  /** Same as HTML `input` max attribute. */
  max?: number

  /** Same as HTML `input` step attribute. */
  step?: number

  /** Same as HTML `input` disabled attribute. */
  disabled?: boolean

  /** Displays one or more help messages in a tooltip by hovering over a help icon inside the input. */
  help?: string | string[]

  /** Inverts the knob color and add a background color to the label. */
  inverted?: boolean

  /** Shows the label in a tooltip instead of next to the slider. */
  labelInTooltip?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  min: 0,
  max: 100,
  step: 1,
  help: '',
})

/** The value of the slider. */
const model = defineModel<number>({ required: true })

const id = useId()
const width = ref(1)
const slider = useTemplateRef('slider')

const fillWidth = computed(() => {
  const value = ((model.value - props.min) / (props.max - props.min)) * 100

  const sliderWidth = width.value ?? 1
  const radius = 7 // radius of the slider button

  // Determine how many pixels should be adjusted horizontally.
  const multi = (radius * 2) / 100
  const adjustPixels = multi * value - radius

  // Convert pixels to percentage of slider width.
  const adjustPercentage = (100 / sliderWidth) * adjustPixels

  return value - adjustPercentage
})

const referenceStyle = computed(
  (): CSSProperties => ({
    left: `${fillWidth.value}%`,
    position: 'absolute',
    transform: 'translate(-50%, -50%)',
    top: '-5px',
    'z-index': 0,
  }),
)

function updateWidth(event: DOMRectReadOnly) {
  width.value = event.width
}

const tooltip = useTemplateRef('tooltip')

function onInput(value: number) {
  model.value = value
  tooltip.value?.popper?.updatePopper()
}

function showTooltip() {
  if (props.labelInTooltip) {
    tooltip.value?.popper?.show()
  }
}

function hideTooltip() {
  tooltip.value?.popper?.hide()
}

onMounted(() => useResizeObserver(slider, updateWidth))

defineExpose({
  onInput,
})
</script>

<style>
.v-slider-wrapper {
  height: 40px;
  display: flex;
  align-items: center;

  label {
    padding: 0 3px;
    margin: 0 5px;
    min-width: 35px;
    border-radius: 4px;
    text-align: center;
  }

  &.is-inverted {
    .v-slider__inner {
      &::-webkit-slider-thumb {
        background: #fff;
        border: 2px solid var(--color-primary);
      }

      &::-moz-range-thumb {
        background: #fff;
        border: 2px solid var(--color-primary);
      }
    }

    label {
      background-color: var(--color-grey-12);
      margin: 0 10px;
    }
  }

  &.is-disabled {
    .v-slider__fill {
      background-color: var(--color-grey-40);
    }

    .v-slider__inner {
      &::-webkit-slider-thumb {
        border: 2px solid var(--color-grey-40);
        background: var(--color-grey-12);
      }

      &::-moz-range-thumb {
        border: 2px solid var(--color-grey-40);
        background: var(--color-grey-12);
      }
    }
  }

  &.label-in-tooltip {
    .v-icon-help {
      margin-left: 10px;
    }
  }
}

.v-slider {
  position: relative;
  display: flex;
  align-items: center;
  background-color: var(--color-grey-12);
  width: 100%;
  height: 6px;
  border-radius: 6px;
}

.v-slider__fill {
  position: absolute;
  height: 6px;
  background-color: var(--color-primary);
  border-radius: 6px;
}

.v-slider__inner {
  appearance: none;
  width: 100%;
  height: 6px;
  background: transparent;
  outline: none;
  z-index: 1;
  margin: 0;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #fff;
    border: 2px solid #fff;
    background: var(--color-primary);
    box-shadow: var(--box-shadow-light);
    cursor: grab;
    z-index: 999;
  }

  &::-moz-range-thumb {
    appearance: none;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #fff;
    border: 2px solid #fff;
    background: var(--color-primary);
    box-shadow: var(--box-shadow-light);
    cursor: grab;
  }
}
</style>
